import React, { useEffect } from 'react';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { setGrowthBookInstance } from '../services/apiUrlResolver';
import logger from '../utils/logger';
// Create GrowthBook instance
const growthbook = new GrowthBook({
  apiHost: import.meta.env.VITE_GROWTHBOOK_API_HOST,
  clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY,
  enableDevMode: import.meta.env.MODE === 'development',
  trackingCallback: (experiment, result) => {
    // Analytics integration point
    logger.log('GrowthBook Experiment:', { experiment, result });
    
    // TODO: Integrate with existing analytics (e.g., Google Analytics, Mixpanel)
    if (window.gtag) {
      window.gtag('event', 'experiment_viewed', {
        experiment_id: experiment.key,
        variation_id: result.variationId,
      });
    }
  },
});

/**
 * GrowthBook Provider Component
 * Wraps the app to provide feature flag functionality
 */
export const AppGrowthBookProvider = ({ children }) => {
  useEffect(() => {
    // Set global GrowthBook instance for utility functions
    setGrowthBookInstance(growthbook);
    
    // Load feature definitions on mount
    growthbook.loadFeatures();
    
    // Set user attributes for targeting
    const setUserAttributes = () => {
      // Get user data from Auth0 localStorage keys
      const userEmail = localStorage.getItem('AUTH_USER_EMAIL');
      const userSub = localStorage.getItem('AUTH_USER_SUB');
      const userRole = localStorage.getItem('AUTH_PACIFY_ROLE');
      const userProfile = localStorage.getItem('AUTH_USER_PROFILE');
      
      let parsedProfile = {};
      try {
        parsedProfile = JSON.parse(userProfile || '{}');
      } catch (e) {
        logger.log('[GrowthBook] Failed to parse user profile:', e);
      }
      
      // Extract user ID from Auth0 sub (format: "auth0|123456")
      const userId = userSub ? userSub.split('|')[1] || userSub : 'anonymous';
      
      const attributes = {
        id: userId,
        email: userEmail || '',
        role_id: userRole ? parseInt(userRole) : null,
        auth0_sub: userSub || '',
        environment: import.meta.env.MODE,
        // Add organization ID if available from profile
        org_id: parsedProfile.organization_id || parsedProfile.org_id || null,
      };
      
      logger.log('[GrowthBook] Setting user attributes:', attributes);
      growthbook.setAttributes(attributes);
    };

    setUserAttributes();
    
    // Listen for Auth0 session changes
    const handleStorageChange = (e) => {
      // Re-set attributes when any Auth0 key changes
      if (e.key && e.key.startsWith('AUTH_')) {
        logger.log('[GrowthBook] Auth storage changed, updating attributes');
        setUserAttributes();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      growthbook.destroy();
    };
  }, []);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      {children}
    </GrowthBookProvider>
  );
};

export default AppGrowthBookProvider;